
import { useState } from 'react';
import { Invoice, Family } from '@/types/models';
import { InvoiceService } from '@/services/invoiceService';
import { InvoiceFormValues } from '../InvoiceForm';
import { toast } from 'sonner';
import { parseCurrencyToCents } from '@/lib/format';

export const useInvoiceOperations = (organizationId: string | null) => {
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isAddInvoiceDialogOpen, setIsAddInvoiceDialogOpen] = useState(false);
  const [isEditInvoiceDialogOpen, setIsEditInvoiceDialogOpen] = useState(false);
  const [isDeleteInvoiceDialogOpen, setIsDeleteInvoiceDialogOpen] = useState(false);

  const handleAddInvoice = () => {
    setSelectedInvoice(null);
    setIsAddInvoiceDialogOpen(true);
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setIsEditInvoiceDialogOpen(true);
  };

  const handleDeleteInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setIsDeleteInvoiceDialogOpen(true);
  };

  const handleCreateInvoice = async (data: InvoiceFormValues) => {
    if (!organizationId) {
      toast.error("No organization found", {
        description: "Please check your account settings"
      });
      return;
    }
    
    try {
      toast.loading("Creating invoice...");
      
      // Parse the currency string to cents properly
      const totalCents = parseCurrencyToCents(data.total);
      
      const invoiceData = {
        family_id: data.family_id,
        organization_id: organizationId,
        total_cents: totalCents,
        due_date: data.due_date.toISOString().split('T')[0],
        status: data.status
      };

      const newInvoice = await InvoiceService.createInvoice(invoiceData);
      
      if (newInvoice) {
        toast.dismiss();
        toast.success("Invoice created successfully");
        setIsAddInvoiceDialogOpen(false);
        return newInvoice;
      } else {
        throw new Error("Failed to create invoice");
      }
    } catch (error) {
      toast.dismiss();
      console.error("Error creating invoice:", error);
      toast.error("Failed to create invoice");
      return null;
    }
  };

  const handleUpdateInvoice = async (data: InvoiceFormValues) => {
    if (!selectedInvoice || !organizationId) return null;
    
    try {
      toast.loading("Updating invoice...");
      
      // Parse the currency string to cents properly
      const totalCents = parseCurrencyToCents(data.total);
      
      const invoiceUpdates = {
        family_id: data.family_id,
        total_cents: totalCents,
        due_date: data.due_date.toISOString().split('T')[0],
        status: data.status
      };
      
      const updatedInvoice = await InvoiceService.updateInvoice(selectedInvoice.id, invoiceUpdates);
      
      if (updatedInvoice) {
        toast.dismiss();
        toast.success("Invoice updated successfully");
        setIsEditInvoiceDialogOpen(false);
        return updatedInvoice;
      } else {
        throw new Error("Failed to update invoice");
      }
    } catch (error) {
      toast.dismiss();
      console.error("Error updating invoice:", error);
      toast.error("Failed to update invoice");
      return null;
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedInvoice) return;
    try {
      toast.loading("Deleting invoice...");
      const success = await InvoiceService.deleteInvoice(selectedInvoice.id);
      if (success) {
        toast.dismiss();
        toast.success("Invoice deleted successfully");
        setIsDeleteInvoiceDialogOpen(false);
        return true;
      } else {
        throw new Error("Failed to delete invoice");
      }
    } catch (error) {
      toast.dismiss();
      console.error("Error deleting invoice:", error);
      toast.error("Failed to delete invoice");
      return false;
    }
  };

  return {
    selectedInvoice,
    isAddInvoiceDialogOpen,
    setIsAddInvoiceDialogOpen,
    isEditInvoiceDialogOpen,
    setIsEditInvoiceDialogOpen,
    isDeleteInvoiceDialogOpen,
    setIsDeleteInvoiceDialogOpen,
    handleAddInvoice,
    handleEditInvoice,
    handleDeleteInvoice,
    handleCreateInvoice,
    handleUpdateInvoice,
    handleConfirmDelete,
  };
};
